/**
 * How an organisation's reported hours are rounded. A mirror of the server's
 * `src/missions/rounding.ts`: the sheet is drawn there and the screens here, and
 * the two must give the same figure for the same time. Both have the same test
 * cases for that reason — change one, change the other.
 *
 * Raw time is never rounded. Time is first taken to the nearest whole minute.
 */
export type RoundingMode = "none" | "nearest" | "up" | "down";
export type RoundingScope = "perLeg" | "total";

export type RoundingRule = { mode: RoundingMode; incrementMinutes: number; scope: RoundingScope };

export const NO_ROUNDING: RoundingRule = { mode: "none", incrementMinutes: 6, scope: "perLeg" };

export const isRounding = (rule: RoundingRule) => rule.mode !== "none";

export const wholeMinutes = (seconds: number) => Math.max(0, Math.round(seconds / 60));

export function roundMinutes(minutes: number, rule: RoundingRule): number {
  if (rule.mode === "none" || minutes <= 0) return minutes;
  const steps = minutes / rule.incrementMinutes;
  const rounded = rule.mode === "up" ? Math.ceil(steps) : rule.mode === "down" ? Math.floor(steps) : Math.floor(steps + 0.5);
  return rounded * rule.incrementMinutes;
}

/** Minutes to print for each run, and for the day. */
export function reportRuns(runSeconds: number[], rule: RoundingRule): { perRun: number[]; total: number } {
  const exact = wholeMinutes(runSeconds.reduce((a, b) => a + b, 0));
  const runs = runSeconds.map(wholeMinutes);
  if (!isRounding(rule)) return { perRun: runs, total: exact };
  if (rule.scope === "perLeg") {
    const rounded = runs.map((m) => roundMinutes(m, rule));
    return { perRun: rounded, total: rounded.reduce((a, b) => a + b, 0) };
  }
  return { perRun: runs, total: roundMinutes(exact, rule) };
}

/** One place for whole tenths, two otherwise; unrounded time keeps the app's two. */
export const hoursDecimals = (rule: RoundingRule): 1 | 2 =>
  isRounding(rule) && rule.incrementMinutes % 6 === 0 ? 1 : 2;

export const formatHours = (minutes: number, decimals: number): string => (minutes / 60).toFixed(decimals);

/** The same sentence the sheet carries, so the screen explains itself the same way. */
export function describeRule(label: string, rule: RoundingRule): string | null {
  if (rule.mode === "none") return null;
  const step = `${Number((rule.incrementMinutes / 60).toFixed(4))} h`;
  const how = { up: "rounded up to the next", down: "rounded down to the", nearest: "rounded to the nearest" }[rule.mode];
  return `${label} ${how} ${step}${rule.scope === "perLeg" ? " per leg" : " on the total"}.`;
}

/** Both rules in one sentence when they match, as the sheet does. */
export function describeRules(engine: RoundingRule, flight: RoundingRule): string[] {
  const same =
    isRounding(engine) &&
    engine.mode === flight.mode &&
    engine.incrementMinutes === flight.incrementMinutes &&
    engine.scope === flight.scope;
  const notes = same
    ? [describeRule("Engine and flight time", engine)]
    : [describeRule("Engine time", engine), describeRule("Flight time", flight)];
  return notes.filter((n): n is string => n !== null);
}
